import { useEffect, useRef, useState } from "react";
import { 
  Bot, 
  User, 
  Send, 
  Search, 
  Sparkles, 
  Brain,
  Circle,
  RefreshCw,
  AlertCircle,
  Calendar,
  CheckCircle,
  XCircle,
  Clock
} from "lucide-react";

// API Functions
import { TrmResultCard } from "./trmCards";
import { post } from "../../api/http";

/**
 * Ask the TRM assistant. Backed by mock fixtures shaped like the uscis_trm star
 * schema (see backend/apis/trm_insights.py), so switching to live portfolio data
 * is a change in that module only — this call and the cards stay as they are.
 */
async function askTrm(query) {
  // actor lets the backend answer "what is assigned to me" against the workflow
  // directory; every other intent ignores it.
  return post("/trm/insights/ask", { query, actor: currentActor() });
}

async function analyzeTool(toolName) {
  const response = await fetch(`/api/tool/analyze-tool`, {
    method: "POST",
    headers: { 
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify({ tool_name: toolName }),
  });

  if (!response.ok) {
    throw new Error(`Failed to analyze tool: ${response.statusText}`);
  }

  return response.json();
}

async function getRecommendations(toolName, description = "") {
  const response = await fetch(`/api/search_tool/recommend`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify({
      tool_name: toolName,
      description: description || "string"
    }),
  });

  if (!response.ok) {
    throw new Error(`Failed to get recommendations: ${response.statusText}`);
  }

  return response.json();
}

async function naturalLanguageQuery(query) {
  const response = await fetch(`/api/tool/nl-query`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify({ query }),
  });

  if (!response.ok) {
    throw new Error(`Failed to process query: ${response.statusText}`);
  }

  return response.json();
}

function parseIntent(text) {
  const t = text.trim();

  // TRM portfolio questions are checked first. They have to outrank the tool
  // patterns below, because the catch-all at the end of this function treats any
  // unrecognised input as a tool name — "show me shelfware" would otherwise be
  // looked up as a product called "show me shelfware".
  const trmPatterns = [
    /consolidat|overlap|redundan|duplicate\s+tool|rationalis|rationaliz/i,
    /shelfware|unused\s+licen|under[\s-]?utilis|under[\s-]?utiliz|wasted\s+spend|idle\s+licen/i,
    /how\s+many\s+licen|licen[cs]e\s+(count|position|utilis|utiliz)|seats?\s+(are\s+)?(unused|idle|active)/i,
    /renew|contract\s+(end|expir)|coming\s+up\s+for/i,
    /portfolio\s+(overview|summary|snapshot|health)|state\s+of\s+the\s+portfolio|total\s+(annual\s+)?spend/i,
    /savings?\s+opportunit|where\s+(can|could)\s+we\s+save|cost\s+reduction/i,
  ];
  if (trmPatterns.some((re) => re.test(t))) {
    return { type: "trm", query: t };
  }

  // Check for natural language queries (questions about tools)
  const nlQueryPatterns = [
    /what\s+tools?\s+(?:are|is)\s+expir/i,
    /show\s+(?:me\s+)?(?:all\s+)?(?:tools?\s+)?expir/i,
    /(?:list|show|find)\s+(?:all\s+)?(?:tools?\s+)?(?:with|that|for)\s+(?:hipaa|fisma|fedramp|soc|pci|iso|complian)/i,
    /how\s+many\s+tools?/i,
    /what\s+(?:database|security|monitoring|cloud|devops|analytics)\s+tools?/i,
    /show\s+(?:me\s+)?(?:all\s+)?(?:active|retired|deprecated)\s+tools?/i,
    /tools?\s+(?:in|for)\s+(?:the\s+)?(?:database|security|monitoring|cloud|devops|analytics|itsm|collaboration|networking|automation)/i,
    /renew(?:al|ing)/i,
    /upcoming\s+(?:expir|renew)/i,
  ];

  const isNLQuery = nlQueryPatterns.some(pattern => pattern.test(t));

  if (isNLQuery) {
    return { type: "nl-query", query: t };
  }

  // Check for explicit commands
  const explicitCommand =
    t.match(/^analy[sz]e\s+(?:tool\s+)?(.+)$/i) ||
    t.match(/^check\s+(.+)$/i) ||
    t.match(/^status\s+of\s+(.+)$/i) ||
    t.match(/^find\s+alternatives?\s+(?:for\s+)?(.+)$/i) ||
    t.match(/^recommend(?:ations)?\s+(?:for\s+)?(.+)$/i);

  if (explicitCommand) {
    return { type: "analyze", tool: explicitCommand[1].trim() };
  }

  // Check for common non-tool phrases (greetings, questions about the bot, etc.)
  const nonToolPhrases = [
    /^(hi|hello|hey|greetings)/i,
    /^(help|what can you do|how does this work)/i,
    /^(thanks|thank you|bye|goodbye)/i,
    /^(who are you|what are you)/i,
  ];

  const isNonToolPhrase = nonToolPhrases.some(pattern => pattern.test(t));

  if (isNonToolPhrase) {
    // Routed to the assistant rather than a canned local reply, so a greeting
    // returns the actual capability list and current portfolio figures.
    return { type: "trm", query: t };
  }

  // Default to the TRM assistant.
  //
  // This used to fall through to /api/tool/analyze-tool, which returns
  // 500 "TRM data not available" — its trm_tools table is empty — so anything the
  // patterns above didn't catch produced nothing at all. The TRM service answers
  // from the portfolio data, resolves a bare product name ("Tableau") to that
  // product, and never dead-ends: an unrecognised question still gets the
  // portfolio picture plus a note on what else it can answer.
  //
  // The legacy analyzer is still reachable via an explicit "analyze X" / "check X"
  // command, matched above.
  return { type: "trm", query: t };
}

function StatusChip({ status }) {
  const statusConfig = {
    Active: { 
      background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))', 
      color: 'var(--cares-green)', 
      border: 'color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))',
      Icon: CheckCircle
    },
    Expired: { 
      background: 'color-mix(in srgb, var(--cares-magenta) 22%, var(--cares-surface-card))', 
      color: 'var(--cares-orange)', 
      border: 'var(--cares-magenta)',
      Icon: XCircle
    },
    Retired: { 
      background: 'color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))', 
      color: 'var(--cares-amber)', 
      border: 'color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))',
      Icon: Clock
    },
    Unknown: { 
      background: 'var(--cares-surface-board)', 
      color: 'var(--cares-text-3)', 
      border: 'var(--cares-line)',
      Icon: AlertCircle
    },
  };

  const config = statusConfig[status] || statusConfig.Unknown;
  const IconComponent = config.Icon;

  return (
    <span style={{
      fontSize: '12px',
      padding: '4px 12px',
      borderRadius: '20px',
      border: `1px solid ${config.border}`,
      backgroundColor: config.background,
      color: config.color,
      fontWeight: '500',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px'
    }}>
      <IconComponent size={12} />
      {status || "Unknown"}
    </span>
  );
}

function ResultCard({ data }) {
  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  // Handle the response structure from the API
  const matches = data.matches || [];
  const duplicates = [...(data.duplicates_by_name || []), ...(data.duplicates_by_description || [])];
  
  // Show the first match as the main result
  const primaryMatch = matches[0] || duplicates[0];
  
  if (!primaryMatch) {
    return (
      <div style={{
        backgroundColor: 'var(--cares-surface-card)',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid var(--cares-line)',
        maxWidth: '600px',
        padding: '16px'
      }}>
        <p style={{ color: 'var(--cares-text-3)', fontSize: '14px' }}>No matching tools found.</p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'var(--cares-surface-card)',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid var(--cares-line)',
      maxWidth: '600px',
      overflow: 'hidden'
    }}>
      <div style={{
        backgroundColor: 'var(--cares-surface-board)',
        padding: '12px 16px',
        borderBottom: '1px solid var(--cares-line)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h3 style={{ fontWeight: '600', color: 'var(--cares-text-2)', fontSize: '14px', margin: 0 }}>
          {primaryMatch.tool_name || "—"}
        </h3>
        <StatusChip status={primaryMatch.status} />
      </div>
      <div style={{ padding: '16px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', fontSize: '14px' }}>
          <div>
            <p style={{ color: 'var(--cares-text-label)', fontSize: '12px', marginBottom: '4px' }}>
              <Calendar size={12} style={{ display: 'inline', marginRight: '4px' }} />
              Procured Date
            </p>
            <p style={{ fontWeight: '500', color: 'var(--cares-text-2)' }}>{fmt(primaryMatch.procured_date)}</p>
          </div>
          <div>
            <p style={{ color: 'var(--cares-text-label)', fontSize: '12px', marginBottom: '4px' }}>
              <Clock size={12} style={{ display: 'inline', marginRight: '4px' }} />
              Expiry Date
            </p>
            <p style={{ fontWeight: '500', color: 'var(--cares-text-2)' }}>{fmt(primaryMatch.expiry_date)}</p>
          </div>
          <div>
            <p style={{ color: 'var(--cares-text-label)', fontSize: '12px', marginBottom: '4px' }}>Matched With</p>
            <p style={{ fontWeight: '500', color: 'var(--cares-text-2)' }}>{primaryMatch.matched_with || "—"}</p>
          </div>
          <div>
            <p style={{ color: 'var(--cares-text-label)', fontSize: '12px', marginBottom: '4px' }}>Similarity Score</p>
            <p style={{ fontWeight: '500', color: 'var(--cares-text-2)' }}>
              {primaryMatch.similarity_score ? `${(primaryMatch.similarity_score * 100).toFixed(0)}%` : "—"}
            </p>
          </div>
        </div>
        
        {primaryMatch.explanation && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: 'color-mix(in srgb, var(--cares-teal) 14%, var(--cares-surface-card))',
            borderRadius: '8px',
            border: '1px solid var(--cares-blue)'
          }}>
            <p style={{ fontSize: '14px', color: 'var(--cares-text-3)', margin: 0 }}>{primaryMatch.explanation}</p>
          </div>
        )}

        {/* Show count of additional matches */}
        {matches.length > 1 && (
          <div style={{
            marginTop: '12px',
            padding: '8px',
            backgroundColor: 'var(--cares-surface-board)',
            borderRadius: '4px',
            fontSize: '12px',
            color: 'var(--cares-text-3)'
          }}>
            Found {matches.length} matching tools and {duplicates.length} potential duplicates
          </div>
        )}
      </div>
    </div>
  );
}

function RecommendationCard({ data }) {
  return (
    <div style={{
      backgroundColor: 'var(--cares-surface-card)',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid var(--cares-line)',
      maxWidth: '600px',
      overflow: 'hidden'
    }}>
      <div style={{
        backgroundColor: 'var(--cares-orange)',
        padding: '12px 16px',
        borderBottom: '1px solid var(--cares-line)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Brain size={16} color="var(--cares-text-max)" />
        <h3 style={{ fontWeight: '600', color: 'var(--cares-text-max)', fontSize: '14px', margin: 0 }}>
          Tool Alternatives for: {data.input_tool}
        </h3>
      </div>
      <div style={{ padding: '16px' }}>
        {data.recommendations && data.recommendations.length > 0 ? (
          <div>
            <p style={{ color: 'var(--cares-text-label)', fontSize: '12px', marginBottom: '12px' }}>
              Recommended alternatives:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {data.recommendations.map((rec, index) => (
                <div key={index} style={{
                  padding: '12px',
                  backgroundColor: 'var(--cares-surface-board)',
                  borderRadius: '8px',
                  border: '1px solid var(--cares-line)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <span style={{
                      backgroundColor: 'var(--cares-blue)',
                      color: 'var(--cares-text-max)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      flexShrink: 0
                    }}>
                      {index + 1}
                    </span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '14px', color: 'var(--cares-text-2)', fontWeight: '500', display: 'block' }}>
                        {rec["tool name"] || rec.tool_name || "Unknown Tool"}
                      </span>
                      {rec.reason && (
                        <span style={{ fontSize: '12px', color: 'var(--cares-text-3)', marginTop: '4px', display: 'block' }}>
                          {rec.reason}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p style={{ fontSize: '14px', color: 'var(--cares-text-3)', fontStyle: 'italic' }}>
            No recommendations found for this tool.
          </p>
        )}
      </div>
    </div>
  );
}

function NLQueryResultCard({ data }) {
  return (
    <div style={{
      backgroundColor: 'var(--cares-surface-card)',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid var(--cares-line)',
      maxWidth: '600px',
      overflow: 'hidden'
    }}>
      <div style={{
        backgroundColor: 'var(--cares-green)',
        padding: '12px 16px',
        borderBottom: '1px solid var(--cares-line)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Sparkles size={16} color="var(--cares-text-max)" />
        <h3 style={{ fontWeight: '600', color: 'var(--cares-text-max)', fontSize: '14px', margin: 0 }}>
          Query Results
        </h3>
      </div>
      <div style={{ padding: '16px' }}>
        {/* Answer */}
        <div style={{
          backgroundColor: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
          border: '1px solid color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))'
        }}>
          <p style={{ fontSize: '14px', color: 'var(--cares-green)', margin: 0, lineHeight: '1.5' }}>
            {data.answer}
          </p>
        </div>

        {/* Tools List */}
        {data.tools && data.tools.length > 0 && (
          <div>
            <h4 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--cares-text-3)', marginBottom: '12px' }}>
              📋 Tools ({data.tools.length})
            </h4>
            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              {data.tools.map((tool, index) => (
                <div key={index} style={{
                  padding: '12px',
                  backgroundColor: 'var(--cares-surface-board)',
                  borderRadius: '6px',
                  border: '1px solid var(--cares-line-card)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-text-2)' }}>
                      {tool.tool_name}
                    </span>
                    {tool.status && (
                      <span style={{
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: tool.status === 'Active' ? 'color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))' : 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
                        color: tool.status === 'Active' ? 'var(--cares-green)' : 'var(--cares-orange)',
                        fontWeight: '600'
                      }}>
                        {tool.status}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px', color: 'var(--cares-text-label)' }}>
                    {tool.category && (
                      <span>📦 {tool.category}</span>
                    )}
                    {tool.expiry_date && (
                      <span style={{
                        color: tool.days_until_expiry && tool.days_until_expiry < 30 ? 'var(--cares-orange)' : 'var(--cares-text-label)'
                      }}>
                        📅 Expires: {new Date(tool.expiry_date).toLocaleDateString()}
                        {tool.days_until_expiry && ` (${tool.days_until_expiry} days)`}
                      </span>
                    )}
                    {tool.compliance_tags && (
                      <span>🛡️ {tool.compliance_tags}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        {data.metadata && Object.keys(data.metadata).length > 0 && (
          <div style={{
            marginTop: '12px',
            paddingTop: '12px',
            borderTop: '1px solid var(--cares-line-card)',
            fontSize: '12px',
            color: 'var(--cares-text-label)'
          }}>
            {data.metadata.total_count !== undefined && (
              <span>Total: {data.metadata.total_count}</span>
            )}
            {data.metadata.urgent_count !== undefined && data.metadata.urgent_count > 0 && (
              <span style={{ marginLeft: '12px', color: 'var(--cares-orange)', fontWeight: '600' }}>
                ⚠️ Urgent: {data.metadata.urgent_count}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Renders **bold** and "- " bullets from an answer string.
 *
 * Deliberately not a markdown library and deliberately not dangerouslySetInnerHTML:
 * the answers only ever use these two constructs, and building React nodes from a
 * split keeps the rendering path free of raw HTML entirely.
 */
/** The signed-in user's name, for questions like "what is assigned to me". */
function currentActor() {
  try {
    const raw = window.localStorage.getItem("currentUser");
    if (!raw) return null;
    const u = JSON.parse(raw);
    return u.name || u.email || null;
  } catch {
    return null;
  }
}

// The only answer kinds with a visual card. Everything else renders as prose.
const CARD_KINDS = new Set(["consolidation", "shelfware", "licenses", "renewals"]);

function RichText({ text }) {
  const bold = (line, keyPrefix) =>
    String(line).split(/\*\*(.+?)\*\*/g).map((part, i) =>
      i % 2 === 1
        ? <strong key={`${keyPrefix}-${i}`} style={{ fontWeight: 700 }}>{part}</strong>
        : <span key={`${keyPrefix}-${i}`}>{part}</span>);

  return (
    <>
      {String(text ?? "").split("\n").map((line, i) => {
        const numbered = line.trimStart().match(/^(\d+)\.\s+(.*)$/);
        if (numbered) {
          return (
            <div key={i} style={{ display: "flex", gap: 7, margin: "3px 0 3px 2px" }}>
              <span style={{ color: "var(--cares-text-note)", flexShrink: 0, fontWeight: 600 }}>
                {numbered[1]}.
              </span>
              <span>{bold(numbered[2], i)}</span>
            </div>
          );
        }
        const bullet = line.trimStart().startsWith("- ");
        if (bullet) {
          return (
            <div key={i} style={{ display: "flex", gap: 6, margin: "2px 0 2px 2px" }}>
              <span style={{ color: "var(--cares-text-note)", flexShrink: 0 }}>•</span>
              <span>{bold(line.trimStart().slice(2), i)}</span>
            </div>
          );
        }
        if (!line.trim()) return <div key={i} style={{ height: 7 }} />;
        return <div key={i}>{bold(line, i)}</div>;
      })}
    </>
  );
}

function Message({ role, children, error, onRetry, isTyping }) {
  const isUser = role === "user";

  const messageContainerStyle = {
    display: 'flex',
    justifyContent: isUser ? 'flex-end' : 'flex-start',
    marginBottom: '16px'
  };

  const messageWrapperStyle = {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    maxWidth: '70%',
    flexDirection: isUser ? 'row-reverse' : 'row'
  };

  const avatarStyle = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--cares-text-max)',
    fontSize: '12px',
    fontWeight: 'bold',
    flexShrink: 0,
    backgroundColor: isUser ? 'var(--cares-orange)' : 'var(--cares-blue)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
  };

  const messageBubbleStyle = {
    padding: '12px 16px',
    borderRadius: '16px',
    backgroundColor: isUser ? 'var(--cares-orange)' : 'var(--cares-surface-board)',
    color: isUser ? 'var(--cares-text-max)' : 'var(--cares-text-2)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    borderBottomRightRadius: isUser ? '4px' : '16px',
    borderBottomLeftRadius: isUser ? '16px' : '4px'
  };

  const typingDotStyle = {
    width: '8px',
    height: '8px',
    backgroundColor: 'var(--cares-surface-raised)',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'bounce 1.4s infinite ease-in-out'
  };

  return (
    <>
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <div style={messageContainerStyle}>
        <div style={messageWrapperStyle}>
          <div style={avatarStyle}>
            {isUser ? <User size={16} /> : <Bot size={16} />}
          </div>
          <div style={messageBubbleStyle}>
            {isTyping ? (
              <div style={{ display: 'flex', gap: '4px', padding: '4px 0' }}>
                <span style={{ ...typingDotStyle, animationDelay: '0s' }}></span>
                <span style={{ ...typingDotStyle, animationDelay: '0.2s' }}></span>
                <span style={{ ...typingDotStyle, animationDelay: '0.4s' }}></span>
              </div>
            ) : (
              <div style={{ fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                {typeof children === "string" ? <RichText text={children} /> : children}
                {error && onRetry && (
                  <button
                    style={{
                      marginTop: '8px',
                      fontSize: '12px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      textDecoration: 'underline',
                      opacity: '0.8',
                      cursor: 'pointer',
                      background: 'none',
                      border: 'none',
                      color: 'inherit'
                    }}
                    onClick={onRetry}
                  >
                    <RefreshCw size={12} />
                    Retry
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * @param {boolean} compact - render to fill a fixed-height container (the floating
 *   panel) instead of growing with the page. The full-page view keeps its own
 *   min/max height; the panel supplies the height and this fills it.
 */
export default function Chatbot({ compact = false }) {
  const [messages, setMessages] = useState([
    {
      role: "bot", id: "welcome",
      text:
        "I'm the AIEA assistant. I answer questions about the technology portfolio — " +
        "27 products, licences, spend, overlap and renewals.\n\n" +
        "Pick one below, or just name a product like \"Tableau\".",
    },
  ]);

  // Clickable starters. The previous welcome listed queries the legacy tool
  // analyser served, and that endpoint returns 500 because its table is empty —
  // so every suggested example led to a dead end.
  // Ordered so the first chips are the questions with a deadline attached, then
  // the ones about work in flight, then the portfolio. Anything naming a specific
  // record is left out — those are typed, not tapped.
  const SUGGESTIONS = [
    "What should I do first?",
    "What's being actively exploited?",
    "What's going out of support?",
    "What changed this week?",
    "What's assigned to me?",
    "What's stuck?",
    "What's in the pipeline?",
    "What's waiting at the TAB?",
    "What happens after Discovery?",
    "Who owns the security stage?",
    "How many POAMs are overdue?",
    "Do we have SBOMs?",
    "What AI systems do we have?",
    "When do our ATOs expire?",
    "Compare Tableau and Power BI",
    "What should we fund?",
    "Show me consolidation opportunities",
    "What shelfware do we have?",
    "What renews in the next 90 days?",
  ];
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const scrollRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages, loading]);

  // `preset` lets the suggestion chips send a question directly. Guarded with
  // typeof because onClick passes the click event as the first argument when a
  // handler is used bare, which would otherwise be treated as the message text.
  async function send(preset) {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || loading) return;

    const id = crypto.randomUUID();
    setMessages((m) => [...m, { role: "user", id, text }]);
    setInput("");

    setLoading(true);

    const intent = parseIntent(text);

    // TRM portfolio questions
    if (intent.type === "trm") {
      try {
        setMessages((m) => [
          ...m,
          { role: "bot", id: crypto.randomUUID(), text: "searching", isSearching: true },
        ]);
        const res = await askTrm(intent.query);
        setMessages((m) => {
          const filtered = m.filter((msg) => !msg.isSearching);
          return [...filtered, {
            role: "bot", id: crypto.randomUUID(),
            trmKind: res.kind, trmAnswer: res.answer, trmData: res.data,
          }];
        });
      } catch (e) {
        setMessages((m) => {
          const filtered = m.filter((msg) => !msg.isSearching);
          return [...filtered, {
            role: "bot", id: crypto.randomUUID(), error: true,
            text: `Could not reach the TRM service: ${e.message}`,
          }];
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Handle natural language queries
    if (intent.type === "nl-query") {
      try {
        setMessages((m) => [
          ...m,
          { role: "bot", id: crypto.randomUUID(), text: "searching", isSearching: true },
        ]);

        const nlData = await naturalLanguageQuery(intent.query);

        // Remove the "Searching..." message and add the result
        setMessages((m) => {
          const filtered = m.filter(msg => !msg.isSearching);
          return [...filtered, { role: "bot", id: crypto.randomUUID(), nlQueryResult: nlData }];
        });
      } catch (e) {
        setMessages((m) => {
          const filtered = m.filter(msg => !msg.isSearching);
          return [
            ...filtered,
            {
              role: "bot",
              id: crypto.randomUUID(),
              error: true,
              text: `Error: ${e.message}`,
              retry: () => send()
            },
          ];
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (intent.type !== "analyze") {
      setMessages((m) => [
        ...m,
        {
          role: "bot",
          id: crypto.randomUUID(),
          text: "Just type any tool name to analyze it, or ask me questions like:\n\n• What tools are expiring soon?\n• Show me all HIPAA compliant tools\n• How many active tools do we have?\n• What database tools are there?"
        },
      ]);
      setLoading(false);
      return;
    }

    try {
      // First API call: Analyze Tool
      setMessages((m) => [
        ...m,
        { role: "bot", id: crypto.randomUUID(), text: "analyzing", isAnalyzing: true },
      ]);

      const analysisData = await analyzeTool(intent.tool);

      // Remove the "Analyzing..." message and add the result card
      setMessages((m) => {
        const filtered = m.filter(msg => !msg.isAnalyzing);
        return [...filtered, { role: "bot", id: crypto.randomUUID(), card: analysisData }];
      });

      // Second API call: Get Recommendations
      setMessages((m) => [
        ...m,
        { role: "bot", id: crypto.randomUUID(), text: "finding", isFinding: true },
      ]);

      const recommendData = await getRecommendations(intent.tool);

      // Remove the "Finding..." message and add the recommendations card
      setMessages((m) => {
        const filtered = m.filter(msg => !msg.isFinding);
        return [...filtered, { role: "bot", id: crypto.randomUUID(), recommendationCard: recommendData }];
      });

    } catch (e) {
      // Remove any loading messages
      setMessages((m) => {
        const filtered = m.filter(msg => !msg.isAnalyzing && !msg.isFinding);
        return [
          ...filtered,
          {
            role: "bot",
            id: crypto.randomUUID(),
            error: true,
            text: `Error: ${e.message}`,
            retry: () => send() // Add retry functionality
          },
        ];
      });
    } finally {
      setLoading(false);
    }
  }

  const headerStyle = {
    backgroundColor: 'var(--cares-blue)',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  };

  const chatAreaStyle = {
    flex: 1,
    // Without minHeight:0 a flex child refuses to shrink below its content, which
    // pushed the input area out of the panel.
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: compact ? '16px' : '24px',
    backgroundColor: 'var(--cares-surface-board)',
    minHeight: compact ? 0 : '400px',
    maxHeight: compact ? 'none' : 'calc(100vh - 200px)',
    scrollBehavior: 'smooth',
    position: 'relative'
  };

  const inputAreaStyle = {
    flexShrink: 0,
    borderTop: '2px solid var(--cares-line)',
    backgroundColor: 'var(--cares-surface-card)',
    padding: '16px'
  };

  const inputStyle = {
    flex: 1,
    padding: '12px 16px',
    fontSize: '14px',
    border: '2px solid var(--cares-blue)',
    borderRadius: '8px',
    outline: 'none',
    backgroundColor: 'color-mix(in srgb, var(--cares-teal) 14%, var(--cares-surface-card))',
    color: 'var(--cares-text-2)',
    transition: 'all 0.3s ease'
  };

  const inputFocusStyle = {
    ...inputStyle,
    backgroundColor: 'var(--cares-surface-card)',
    borderColor: 'var(--cares-orange)',
    boxShadow: '0 0 0 3px rgba(255, 122, 0, 0.1)'
  };

  const buttonStyle = {
    padding: '12px 20px',
    borderRadius: '8px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: 'none',
    cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
    backgroundColor: loading || !input.trim() ? 'var(--cares-line-strong)' : 'var(--cares-orange)',
    color: 'var(--cares-text-max)',
    fontSize: '14px',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{
      // In the floating panel the wrapper supplies the frame, so fill it rather
      // than imposing 90vh, a 40px top margin and a second rounded shadow — that
      // combination produced a box inside a box with the input area pushed
      // below the panel's bottom edge.
      height: compact ? '100%' : '90vh',
      minHeight: 0,
      backgroundColor: 'var(--cares-surface-card)',
      display: 'flex',
      flexDirection: 'column',
      marginTop: compact ? 0 : '40px',
      borderRadius: compact ? 0 : '12px',
      boxShadow: compact ? 'none' : '0 4px 12px rgba(0,0,0,0.1)',
      overflow: 'hidden',
    }}>
      <style>{`
        /* Custom scrollbar styles */
        .chat-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .chat-scrollbar::-webkit-scrollbar-track {
          background: var(--cares-surface-board);
          border-radius: 10px;
        }
        .chat-scrollbar::-webkit-scrollbar-thumb {
          background: var(--cares-blue);
          border-radius: 10px;
        }
        .chat-scrollbar::-webkit-scrollbar-thumb:hover {
          background: var(--cares-blue);
        }
        
        /* Pulse animation */
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(141, 255, 145, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(141, 255, 145, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(141, 255, 145, 0);
          }
        }
      `}</style>
      
      {/* Header — omitted in compact mode; the floating panel supplies its own,
          and showing both stacked two title bars on top of each other. */}
      {!compact && (
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            backgroundColor: 'var(--cares-surface-card)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Bot size={24} color="var(--cares-blue)" />
          </div>
          <div>
            <h1 style={{ color: 'var(--cares-text-max)', fontWeight: '600', fontSize: '18px', margin: 0 }}>
              Tool Analysis Assistant
            </h1>
           
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Circle 
            size={8} 
            fill="color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))" 
            color="var(--cares-green)"
            style={{
              animation: 'pulse 2s infinite'
            }}
          />
          <span style={{ color: 'var(--cares-text-max)', fontSize: '14px' }}>Online</span>
        </div>
      </div>
      )}

      {/* Chat Area */}
      <div ref={scrollRef} style={chatAreaStyle} className="chat-scrollbar">
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {messages.map((m) => {
            if (m.isAnalyzing) {
              return (
                <div key={m.id} style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--cares-blue)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--cares-text-max)',
                      flexShrink: 0,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      <Bot size={16} />
                    </div>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '16px',
                      backgroundColor: 'var(--cares-surface-board)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Search size={16} color="var(--cares-text-3)" />
                      <span style={{ fontSize: '14px', color: 'var(--cares-text-3)' }}>Analyzing tool...</span>
                    </div>
                  </div>
                </div>
              );
            }
            
            if (m.isFinding) {
              return (
                <div key={m.id} style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--cares-blue)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--cares-text-max)',
                      flexShrink: 0,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      <Bot size={16} />
                    </div>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '16px',
                      backgroundColor: 'var(--cares-surface-board)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Sparkles size={16} color="var(--cares-text-3)" />
                      <span style={{ fontSize: '14px', color: 'var(--cares-text-3)' }}>Finding alternative recommendations...</span>
                    </div>
                  </div>
                </div>
              );
            }

            if (m.isSearching) {
              return (
                <div key={m.id} style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--cares-blue)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--cares-text-max)',
                      flexShrink: 0,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      <Bot size={16} />
                    </div>
                    <div style={{
                      padding: '12px 16px',
                      borderRadius: '16px',
                      backgroundColor: 'var(--cares-surface-board)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Search size={16} color="var(--cares-text-3)" />
                      <span style={{ fontSize: '14px', color: 'var(--cares-text-3)' }}>Searching tools...</span>
                    </div>
                  </div>
                </div>
              );
            }

            if (m.id === "welcome") {
              return (
                <div key={m.id} style={{ marginBottom: 16 }}>
                  <Message role="bot">{m.text}</Message>
                  <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: 8,
                    marginTop: 12, marginLeft: 40,
                  }}>
                    {SUGGESTIONS.map((q) => (
                      <button
                        key={q}
                        onClick={() => send(q)}
                        style={{
                          border: '1px solid color-mix(in srgb, var(--cares-purple) 30%, var(--cares-surface-card))', background: 'color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))',
                          color: 'var(--cares-purple)', borderRadius: 999, padding: '6px 12px',
                          fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              );
            }

            if (m.trmData && CARD_KINDS.has(m.trmKind)) {
              return (
                <div key={m.id} style={{ marginBottom: '16px' }}>
                  <div style={{ maxWidth: '95%' }}>
                    <div style={{
                      marginBottom: '10px', fontSize: '14px', lineHeight: 1.6, color: 'var(--cares-text-3)',
                    }}>
                      <RichText text={m.trmAnswer} />
                    </div>
                    <TrmResultCard kind={m.trmKind} data={m.trmData} />
                  </div>
                </div>
              );
            }

            if (m.trmData) {
              return (
                <Message key={m.id} role="bot">{m.trmAnswer}</Message>
              );
            }

            return m.nlQueryResult ? (
              <div key={m.id} style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                <NLQueryResultCard data={m.nlQueryResult} />
              </div>
            ) : m.card ? (
              <div key={m.id} style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--cares-blue)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cares-text-max)',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}>
                    <Bot size={16} />
                  </div>
                  <ResultCard data={m.card} />
                </div>
              </div>
            ) : m.recommendationCard ? (
              <div key={m.id} style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--cares-blue)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--cares-text-max)',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}>
                    <Bot size={16} />
                  </div>
                  <RecommendationCard data={m.recommendationCard} />
                </div>
              </div>
            ) : (
              <Message key={m.id} role={m.role} error={m.error} onRetry={m.retry}>
                {m.text}
              </Message>
            );
          })}
          {loading && <Message role="bot" isTyping={true} />}
        </div>
      </div>

      {/* Input Area */}
      <div style={inputAreaStyle}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="text"
              style={inputStyle}
              onFocus={(e) => Object.assign(e.target.style, inputFocusStyle)}
              onBlur={(e) => Object.assign(e.target.style, inputStyle)}
              placeholder="Ask about risk, the pipeline, the process, or any product…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              disabled={loading}
            />
            <button
              style={buttonStyle}
              onClick={send}
              disabled={loading || !input.trim()}
              onMouseEnter={(e) => {
                if (!loading && input.trim()) {
                  e.target.style.backgroundColor = 'var(--cares-amber)';
                  e.target.style.transform = 'scale(1.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading && input.trim()) {
                  e.target.style.backgroundColor = 'var(--cares-orange)';
                  e.target.style.transform = 'scale(1)';
                }
              }}
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send
                </>
              )}
            </button>
          </div>
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--cares-text-label)', textAlign: 'center' }}>
            Press Enter to send 
          </div>
        </div>
      </div>
    </div>
  );
}